import type { SelectProps } from '@risk-smart/themed-cloudscape-components/select';
import CSelect from '@risk-smart/themed-cloudscape-components/select';
import { forwardRef, useMemo, useState } from 'react';

import { filterOptionsByVisibleText } from '../option-label-filter/filterOptionsByVisibleText';
import styles from './style.module.scss';

const Select = forwardRef<SelectProps.Ref, SelectProps>(
  (
    { filteringType = 'auto', options, onLoadItems, virtualScroll, ...props },
    ref
  ) => {
    const [filteringText, setFilteringText] = useState('');

    // See filterOptionsByVisibleText for why we override 'auto' filtering.
    const preFilter = filteringType === 'auto';

    const visibleOptions = useMemo(
      () =>
        preFilter
          ? filterOptionsByVisibleText(options, filteringText)
          : options,
      [options, filteringText, preFilter]
    );

    // When we pre-filter (manual mode), Cloudscape's virtual list keeps its
    // scroll window pinned to the unfiltered slice and ignores the narrowed
    // option array we feed it, so typing in the search appears to do nothing
    // and clearing leaves a stale window (RSP-6288). react-virtual@2 only
    // re-windows when Cloudscape owns the filtering ('auto'), which we
    // deliberately don't use.
    //
    // RSP-6288 first disabled virtualScroll for the whole pre-filtered path.
    // Because `filteringType` defaults to 'auto' and no caller overrides it,
    // that silently made every virtualScroll prop in the app dead code — 12k
    // owner options then rendered one DOM node each and took 30+s to open
    // (COI-569). Only the *narrowed* array conflicts with the virtual window:
    // while the filter box is empty, filterOptionsByVisibleText returns
    // `options` untouched, so there is no narrowed slice to go stale against.
    // Gate on that instead — virtualScroll is suppressed only while a filter is
    // actually narrowing, so the virtual list is unmounted for exactly the
    // window in which RSP-6288 misbehaved and is remounted against the full
    // array on clear. Note that jsdom cannot reproduce RSP-6288's symptoms at
    // all (they need real scroll geometry), so that reasoning is argued, not
    // test-proven; see the note in MultiSelectVirtualFilter.test.tsx.
    const isNarrowing = preFilter && filteringText !== '';
    const effectiveVirtualScroll = isNarrowing ? false : virtualScroll;

    return (
      <div className={styles.select}>
        <CSelect
          {...props}
          virtualScroll={effectiveVirtualScroll}
          filteringType={preFilter ? 'manual' : filteringType}
          options={visibleOptions}
          onLoadItems={(event) => {
            if (preFilter) {
              setFilteringText(event.detail.filteringText ?? '');
            }
            onLoadItems?.(event);
          }}
          ref={ref}
        />
      </div>
    );
  }
);
Select.displayName = 'Select';

export default Select;
